import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import "./CompanyAccountingSettings.css";

const API_URL = "/api";

const emptyForm = {
  cash_account_id: "",
  bank_account_id: "",
  accounts_receivable_account_id: "",
  inventory_account_id: "",
  tax_deductible_account_id: "",

  accounts_payable_account_id: "",
  tax_payable_account_id: "",

  sales_revenue_account_id: "",
  sales_discount_account_id: "",
  sales_return_account_id: "",
  cost_of_sales_account_id: "",
  purchase_expense_account_id: "",
  cash_income_account_id: "",
  cash_expense_account_id: "",
  inventory_adjustment_income_account_id: "",
  inventory_adjustment_expense_account_id: "",

  use_perpetual_inventory: true,
  is_active: true,
};

const accountFields = Object.keys(emptyForm).filter(
  (field) =>
    field.endsWith("_account_id")
);

const getHeaders = () => {
  const token = localStorage.getItem("company_token");

  return {
    Authorization: `Bearer ${token}`,
  };
};

const getErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item.msg)
      .join(". ");
  }

  return detail || "No fue posible completar la operación.";
};

const toAccountValue = (value) =>
  value === null || value === undefined
    ? ""
    : String(value);

const normalizeSettings = (data = {}) => ({
  ...emptyForm,
  ...data,

  cash_account_id: toAccountValue(
    data.cash_account_id
  ),
  bank_account_id: toAccountValue(
    data.bank_account_id
  ),
  accounts_receivable_account_id: toAccountValue(
    data.accounts_receivable_account_id
  ),
  inventory_account_id: toAccountValue(
    data.inventory_account_id
  ),
  tax_deductible_account_id: toAccountValue(
    data.tax_deductible_account_id
  ),

  accounts_payable_account_id: toAccountValue(
    data.accounts_payable_account_id
  ),
  tax_payable_account_id: toAccountValue(
    data.tax_payable_account_id
  ),

  sales_revenue_account_id: toAccountValue(
    data.sales_revenue_account_id
  ),
  sales_discount_account_id: toAccountValue(
    data.sales_discount_account_id
  ),
  sales_return_account_id: toAccountValue(
    data.sales_return_account_id
  ),
  cost_of_sales_account_id: toAccountValue(
    data.cost_of_sales_account_id
  ),
  purchase_expense_account_id: toAccountValue(
    data.purchase_expense_account_id
  ),
  cash_income_account_id: toAccountValue(
    data.cash_income_account_id
  ),
  cash_expense_account_id: toAccountValue(
    data.cash_expense_account_id
  ),
  inventory_adjustment_income_account_id: toAccountValue(
    data.inventory_adjustment_income_account_id
  ),
  inventory_adjustment_expense_account_id: toAccountValue(
    data.inventory_adjustment_expense_account_id
  ),

  use_perpetual_inventory:
    data.use_perpetual_inventory ?? true,

  is_active: data.is_active ?? true,
});

function CompanyAccountingSettings({
  readOnly = false,
}) {
  const [form, setForm] = useState(emptyForm);
  const [accounts, setAccounts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [settingsResponse, accountsResponse] =
        await Promise.all([
          axios.get(
            `${API_URL}/settings/accounting`,
            { headers: getHeaders() }
          ),
          axios.get(
            `${API_URL}/accounting/accounts`,
            {
              headers: getHeaders(),
              params: {
                only_active: true,
              },
            }
          ),
        ]);

      setForm(
        normalizeSettings(settingsResponse.data)
      );

      setAccounts(
        Array.isArray(accountsResponse.data)
          ? accountsResponse.data
          : []
      );
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const accountsByType = useMemo(() => {
    const selectableAccounts = accounts.filter(
      (account) =>
        account.is_active &&
        account.allows_posting
    );

    const getByTypes = (...types) =>
      selectableAccounts.filter((account) =>
        types.includes(account.account_type)
      );

    return {
      assets: getByTypes("ACTIVO"),
      liabilities: getByTypes("PASIVO"),
      income: getByTypes("INGRESO"),
      costs: getByTypes("COSTO"),
      expenses: getByTypes("GASTO"),
      costsOrExpenses: getByTypes(
        "COSTO",
        "GASTO"
      ),
    };
  }, [accounts]);

  const handleChange = (event) => {
    const { name, value, type, checked } =
      event.target;

    setForm((current) => ({
      ...current,
      [name]:
        type === "checkbox" ? checked : value,
    }));
  };

  const buildPayload = () => {
    const payload = {
      use_perpetual_inventory:
        form.use_perpetual_inventory,

      is_active: form.is_active,
    };

    accountFields.forEach((field) => {
      payload[field] = form[field]
        ? Number(form[field])
        : null;
    });

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (readOnly) {
      return;
    }
    setSaving(true);

    try {
      const response = await axios.put(
        `${API_URL}/settings/accounting`,
        buildPayload(),
        { headers: getHeaders() }
      );

      setForm(normalizeSettings(response.data));

      toast.success(
        "Configuración contable guardada correctamente."
      );
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const renderAccountOption = (account) => (
    <option key={account.id} value={account.id}>
      {account.code} - {account.name}
    </option>
  );

  const renderSelect = ({
    name,
    label,
    helper,
    options,
    required = false,
  }) => (
    <div className="accounting-setting-field">
      <label htmlFor={name}>
        {label}
        {required && (
          <span className="required-mark"> *</span>
        )}
      </label>

      <select
        id={name}
        name={name}
        value={form[name]}
        onChange={handleChange}
        disabled={readOnly}
      >
        <option value="">
          Selecciona una cuenta
        </option>

        {options.map(renderAccountOption)}
      </select>

      {helper && (
        <small>{helper}</small>
      )}
    </div>
  );

  if (loading) {
    return (
      <section className="company-accounting-settings">
        <div className="accounting-settings-loading">
          Cargando configuración contable...
        </div>
      </section>
    );
  }

  return (
    <section className="company-accounting-settings">
      <div className="accounting-settings-heading">
        <div>
          <span className="accounting-eyebrow">
            CONFIGURACIÓN
          </span>

          <h1>Contabilidad</h1>

          <p>
            Define las cuentas que NetLedger usará
            para crear asientos automáticos.
          </p>
        </div>

        <button
          type="button"
          className="accounting-refresh-button"
          onClick={loadData}
          disabled={saving}
        >
          ↻ Actualizar
        </button>
      </div>

      <div className="accounting-settings-alert">
        <strong>Importante:</strong> estas cuentas
        se usarán en ventas, compras, movimientos
        de caja e impuestos. Selecciona únicamente
        cuentas auxiliares que permitan movimientos.
      </div>

      <form
        className="accounting-settings-form"
        onSubmit={handleSubmit}
      >
        <section className="accounting-settings-card">
          <div className="accounting-card-header">
            <div>
              <h2>Activos y recaudo</h2>
              <p>
                Cuentas donde se registran el efectivo,
                bancos, cartera e inventario.
              </p>
            </div>

            <span className="accounting-card-icon">
              💰
            </span>
          </div>

          <div className="accounting-fields-grid">
            {renderSelect({
              name: "cash_account_id",
              label: "Cuenta de caja",
              helper:
                "Se usa en ventas y movimientos pagados en efectivo.",
              options: accountsByType.assets,
            })}

            {renderSelect({
              name: "bank_account_id",
              label: "Cuenta bancaria",
              helper:
                "Se usa para pagos por tarjeta o transferencia.",
              options: accountsByType.assets,
            })}

            {renderSelect({
              name: "accounts_receivable_account_id",
              label: "Cuentas por cobrar",
              helper:
                "Se usa cuando la venta queda a crédito.",
              options: accountsByType.assets,
            })}

            {renderSelect({
              name: "inventory_account_id",
              label: "Inventario",
              helper:
                "Se acredita al vender y se debita al comprar mercancía.",
              options: accountsByType.assets,
            })}
          </div>
        </section>

        <section className="accounting-settings-card">
          <div className="accounting-card-header">
            <div>
              <h2>Pasivos e impuestos</h2>
              <p>
                Cuentas para obligaciones con proveedores
                y autoridades tributarias.
              </p>
            </div>

            <span className="accounting-card-icon">
              🧾
            </span>
          </div>

          <div className="accounting-fields-grid">
            {renderSelect({
              name: "accounts_payable_account_id",
              label: "Cuentas por pagar",
              helper:
                "Se utiliza en compras realizadas a crédito.",
              options: accountsByType.liabilities,
            })}

            {renderSelect({
              name: "tax_payable_account_id",
              label: "Impuestos por pagar",
              helper:
                "Registra el IVA generado y otros impuestos de venta.",
              options: accountsByType.liabilities,
            })}
            {renderSelect({
              name: "tax_deductible_account_id",
              label: "IVA descontable",
              helper:
                "Se debita al registrar el IVA pagado en compras.",
              options: accountsByType.liabilities,
            })}
          </div>
        </section>

        <section className="accounting-settings-card">
          <div className="accounting-card-header">
            <div>
              <h2>Ventas, costos y devoluciones</h2>
              <p>
                Cuentas que determinan los ingresos,
                costos y ajustes comerciales.
              </p>
            </div>

            <span className="accounting-card-icon">
              📈
            </span>
          </div>

          <div className="accounting-fields-grid">
            {renderSelect({
              name: "sales_revenue_account_id",
              label: "Ingresos por ventas",
              helper:
                "Cuenta acreditada al registrar una venta.",
              options: accountsByType.income,
            })}

            {renderSelect({
              name: "cost_of_sales_account_id",
              label: "Costo de ventas",
              helper:
                "Cuenta debitada al descontar inventario vendido.",
              options: accountsByType.costs,
            })}

            {renderSelect({
              name: "sales_discount_account_id",
              label: "Descuentos en ventas",
              helper:
                "Cuenta para descuentos comerciales otorgados.",
              options: accountsByType.expenses,
            })}

            {renderSelect({
              name: "sales_return_account_id",
              label: "Devoluciones en ventas",
              helper:
                "Cuenta para anulaciones y reintegros de ventas.",
              options: accountsByType.expenses,
            })}

            {renderSelect({
              name: "purchase_expense_account_id",
              label: "Gastos o costos de compra",
              helper:
                "Cuenta para fletes, gastos y ajustes de compra.",
              options: accountsByType.costsOrExpenses,
            })}
          </div>
        </section>
        <section className="accounting-settings-card">
          <div className="accounting-card-header">
            <div>
              <h2>Caja y movimientos manuales</h2>
              <p>
                Contrapartidas contables para ingresos
                y egresos registrados manualmente en caja.
              </p>
            </div>

            <span className="accounting-card-icon">
              💵
            </span>
          </div>

          <div className="accounting-fields-grid">
            {renderSelect({
              name: "cash_income_account_id",
              label: "Ingresos manuales de caja",
              helper:
                "Se acredita cuando registras un ingreso manual.",
              options: accountsByType.income,
            })}

            {renderSelect({
              name: "cash_expense_account_id",
              label: "Egresos manuales de caja",
              helper:
                "Se debita cuando registras un egreso manual.",
              options: accountsByType.expenses,
            })}
          </div>
        </section>
        <section className="accounting-settings-card">
          <div className="accounting-card-header">
            <div>
              <h2>Ajustes de inventario</h2>
              <p>
                Cuentas usadas al registrar sobrantes o
                faltantes manuales de productos.
              </p>
            </div>

            <span className="accounting-card-icon">
              📦
            </span>
          </div>

          <div className="accounting-fields-grid">
            {renderSelect({
              name: "inventory_adjustment_income_account_id",
              label: "Ingresos por sobrantes",
              helper:
                "Se acredita al registrar una entrada manual de inventario.",
              options: accountsByType.income,
            })}

            {renderSelect({
              name: "inventory_adjustment_expense_account_id",
              label: "Gastos por faltantes",
              helper:
                "Se debita al registrar una salida manual de inventario.",
              options: accountsByType.expenses,
            })}
          </div>
        </section>

        <section className="accounting-settings-card">
          <div className="accounting-card-header">
            <div>
              <h2>Políticas contables</h2>
              <p>
                Define cómo se comportará NetLedger
                cuando contabilice operaciones.
              </p>
            </div>

            <span className="accounting-card-icon">
              ⚙
            </span>
          </div>

          <div className="accounting-checkbox-list">
            <label className="accounting-checkbox">
              <input
                type="checkbox"
                name="use_perpetual_inventory"
                checked={
                  form.use_perpetual_inventory
                }
                onChange={handleChange}
              />

              <span>
                <strong>
                  Usar inventario permanente
                </strong>

                <small>
                  Cada venta registrará automáticamente
                  inventario y costo de ventas.
                </small>
              </span>
            </label>

            <label className="accounting-checkbox">
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
              />

              <span>
                <strong>
                  Activar contabilización automática
                </strong>

                <small>
                  Permite que ventas, compras, caja y
                  bancos generen comprobantes al conectar
                  PostingService.
                </small>
              </span>
            </label>
          </div>
        </section>

        <div className="accounting-settings-actions">
          <button
            type="button"
            className="accounting-cancel-button"
            onClick={loadData}
            disabled={saving}
          >
            Actualizar
          </button>

          {!readOnly ? (
            <button
              type="submit"
              className="accounting-save-button"
              disabled={saving}
            >
              {saving
                ? "Guardando..."
                : "Guardar configuración"}
            </button>
          ) : (
            <span className="settings-view-only">
              Solo consulta
            </span>
          )}
        </div>
      </form>
    </section>
  );
}

export default CompanyAccountingSettings;